import { useCallback, useEffect, useRef, useState } from "react";
import { api, resolveTenantSlug } from "../lib/api";

/**
 * Which notification features super-admin has enabled for this store
 * (GET /admin/push/features). Read-only here — the backend enforces the
 * same switches on every notification endpoint; this only drives the UI.
 *
 * One request per store per 15s is shared between the sidebar, the push
 * page and the customers page.
 */
const EMPTY = { bulk: false, scheduled: false, deliveryMode: "live", limits: null, timezone: null, appPushIssue: null };
const TTL_MS = 15000;
const cache = new Map(); // tenant slug → { at, promise }

function fetchFeatures(force) {
  const slug = resolveTenantSlug();
  const hit = cache.get(slug);
  if (!force && hit && Date.now() - hit.at < TTL_MS) return hit.promise;
  const promise = api.get("/admin/push/features").then(({ data }) => ({
    bulk: !!data.bulk_enabled,
    scheduled: !!data.scheduled_enabled,
    deliveryMode: data.delivery_mode || "live",
    // Why notifications cannot reach this store's mobile app (null = ready).
    appPushIssue: data.app_push_issue || null,
    limits: data.limits || null,
    // The store's own time zone — every schedule is entered and shown in it.
    timezone: data.timezone || null,
  }));
  cache.set(slug, { at: Date.now(), promise });
  promise.catch(() => cache.delete(slug));
  return promise;
}

export default function usePushFeatures({ enabled = true } = {}) {
  const [state, setState] = useState({ loading: enabled, error: null, ...EMPTY });
  const alive = useRef(true);

  const load = useCallback(async (force = false) => {
    if (!enabled) return;
    try {
      const f = await fetchFeatures(force);
      if (alive.current) setState({ loading: false, error: null, ...f });
    } catch (e) {
      if (alive.current) {
        setState({
          loading: false, ...EMPTY,
          error: e?.response?.data?.detail || "Could not load notification settings",
        });
      }
    }
  }, [enabled]);

  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; };
  }, [load]);

  return { ...state, anyEnabled: state.bulk || state.scheduled, refresh: () => load(true) };
}
